import { useSelector, useDispatch } from 'react-redux'
import { API, graphqlOperation } from 'aws-amplify'
import { v4 as uuidv4 } from 'uuid'
import { useEffect, useCallback } from 'react'
import { setCreateNewOccupancy } from '../redux/auth/actions'
import { SET_USER_OCCUPANCIES } from '../redux/actionTypes'
import { showBuildingPicker } from '../redux/app/actions'

const createMutation = `mutation CreateAssociation($input: createOccupantInput) {
    createAssociation(input: $input) {
        pk
        sk
        d1
        d2
        firstName
        lastName
        email
        version
        building
        tenant
        client
        property
    }
}`

const listOccupancy = `query($sk: String, $pk: ID) {
    listOccupancy(sk: $sk, pk: $pk) {
        items {
            pk
            sk
            d1
            d2
            roles
            firstName
            lastName
            email
            workPhone {
                number
                extension
            }
            phone {
                number
                extension
            }
            tenant
            building
            property
        }
    }
}`

export const useCreateOccupancy = () => {
    const dispatch = useDispatch()

    const createNewOccupancy = useSelector((state) => state.auth.createNewOccupancy)
    const user = useSelector((state) => state.app.user)
    const data = useSelector((state) => state.auth.data)
    const occupancies = useSelector((state) => state.app.occupancies)

    const getDefaultUserAttributes = useCallback(() => {
        return {
            pk: user.attributes.sub,
            firstName: user.attributes.given_name,
            lastName: user.attributes.family_name,
            email: user.attributes.email,
        }
    }, [user])

    const fetchAndCreateOccupancy = useCallback(async () => {
        const { pk, firstName, lastName, email } = getDefaultUserAttributes()

        const inputData = {
            pk,
            sk: `OCCUPANCY#${data.tenant.pk}`,
            d1: `${data.tenant.d1}#${data.tenant.pk}#${''}#${''}`,
            d2: uuidv4(),
            email,
            firstName,
            lastName,
            registrationDate: Date.now(),
            roles: ['OCCUPANT'],
            version: 1,
        }

        try {
            const res = await API.graphql(graphqlOperation(createMutation, { input: inputData }))

            if (res.data.createAssociation) {
                // Fetch fresh occupancies from the database to ensure we have the latest data
                const resOccupancies = await API.graphql(graphqlOperation(listOccupancy, { pk: pk, sk: 'OCCUPANCY' }))
                const freshOccupancies = resOccupancies.data.listOccupancy.items

                dispatch({
                    type: SET_USER_OCCUPANCIES,
                    data: freshOccupancies,
                })

                // Wait for the next tick to ensure Redux state is updated before showing building picker
                setTimeout(() => {
                    // Only show building picker if user has multiple occupancies
                    if (freshOccupancies.length > 1) {
                        showBuildingPicker(true)
                    }
                }, 100)

                setCreateNewOccupancy(false)
            }
        } catch (e) {
            // console.error('Error creating occupancy: ', e)
            setCreateNewOccupancy(false)
        }
    }, [getDefaultUserAttributes, data, dispatch, user])

    const checkAndCreateRegistrationLinkAssociation = useCallback(async () => {
        // Check if user logged in through a registration link and doesn't have association for this tenant
        let registrationData = data

        // If no data in state, check localStorage for persisted registration data
        if (!registrationData?.tenant && user?.attributes && occupancies) {
            try {
                const storedData = localStorage.getItem('registrationLinkData')
                if (storedData) {
                    registrationData = JSON.parse(storedData)
                }
            } catch (error) {
                console.error('Error parsing stored registration data:', error)
            }
        }

        if (registrationData?.tenant && user?.attributes && occupancies) {
            const { pk, firstName, lastName, email } = getDefaultUserAttributes()

            // Check if user already has an association for this tenant
            const hasExistingAssociation = occupancies.some(occ =>
                occ.sk === `OCCUPANCY#${registrationData.tenant.pk}`
            )

            if (!hasExistingAssociation) {
                const inputData = {
                    pk,
                    sk: `OCCUPANCY#${registrationData.tenant.pk}`,
                    d1: `${registrationData.tenant.d1}#${registrationData.tenant.pk}##`,
                    d2: uuidv4(),
                    email,
                    firstName,
                    lastName,
                    registrationDate: Date.now(),
                    roles: ['OCCUPANT'],
                    version: 1,
                }

                try {
                    const res = await API.graphql(graphqlOperation(createMutation, { input: inputData }))

                    if (res.data.createAssociation) {
                        console.log('Registration link association created successfully')

                        // Fetch fresh occupancies from the database to ensure we have the latest data
                        const resOccupancies = await API.graphql(graphqlOperation(listOccupancy, { pk: pk, sk: 'OCCUPANCY' }))
                        const freshOccupancies = resOccupancies.data.listOccupancy.items

                        // Update Redux with fresh occupancies that include the new association
                        dispatch({
                            type: SET_USER_OCCUPANCIES,
                            data: freshOccupancies,
                        })

                        // FIXED: Improve timing and prevent dashboard flashing
                        // Wait longer and ensure Redux state is fully updated before showing building picker
                        // This prevents the dashboard from flashing before the building selector appears
                        setTimeout(() => {
                            // Only show building picker if user has multiple occupancies
                            if (freshOccupancies.length > 1) {
                                showBuildingPicker(true)
                            }
                        }, 250) // Increased timeout to ensure proper state updates

                        // Clear the stored registration data since it's been processed
                        localStorage.removeItem('registrationLinkData')
                    }
                } catch (error) {
                    console.error('Error creating registration link association:', error)
                }
            }
        }
    }, [getDefaultUserAttributes, data, dispatch, user, occupancies])

    useEffect(() => {
        if (createNewOccupancy) {
            fetchAndCreateOccupancy()
        }
    }, [createNewOccupancy, fetchAndCreateOccupancy])

    // Check for registration link associations when user and occupancies are loaded
    useEffect(() => {
        if (user?.attributes && occupancies && data?.tenant) {
            checkAndCreateRegistrationLinkAssociation()
        }
    }, [user, occupancies, data, checkAndCreateRegistrationLinkAssociation])
}

